import type { ErrorInfo } from 'react';

/**
 * Production error reporting stub.
 * Wire-ready: replace body with your SDK call, e.g.:
 *   Sentry.captureException(error, { extra: { componentStack: info.componentStack } })
 */
export function reportError(error: Error, info: ErrorInfo): void {
  if (import.meta.env.DEV) return; // DEV logging handled by ErrorBoundary before this call
  const payload = {
    message: error.message,
    stack: error.stack,
    componentStack: info.componentStack,
    url: window.location.href,
  };
  // eslint-disable-next-line no-console
  console.error('[error-tracking]', payload);
}

/** Datos de un fallo de API, tal como se registran para diagnóstico. */
interface ApiFailurePayload {
  /** Qué se estaba pidiendo, en términos del dominio (no la URL cruda). */
  context: string;
  message: string;
  /** Código HTTP cuando la petición llegó al servidor; null si fue fallo de red. */
  status: number | null;
  /** Cuerpo de la respuesta de error, cuando el servidor devolvió uno. */
  body: unknown;
  stack?: string;
  url: string;
  timestamp: string;
}

/** Forma mínima de un error de API, para leer status y cuerpo sin depender de la clase. */
interface ApiErrorShape {
  status: number;
  body: unknown;
}

function asApiError(error: unknown): ApiErrorShape | null {
  if (typeof error !== 'object' || error === null) return null;
  const candidate = error as Partial<ApiErrorShape>;
  return typeof candidate.status === 'number'
    ? { status: candidate.status, body: candidate.body }
    : null;
}

function buildApiFailurePayload(context: string, error: unknown): ApiFailurePayload {
  const apiError = asApiError(error);
  return {
    context,
    message: error instanceof Error ? error.message : String(error),
    status: apiError?.status ?? null,
    body: apiError?.body,
    stack: error instanceof Error ? error.stack : undefined,
    url: window.location.href,
    timestamp: new Date().toISOString(),
  };
}

/**
 * Registra un fallo de API con el detalle completo. Se llama junto con el mensaje que ve
 * la persona: el mensaje de pantalla es corto y comprensible, mientras este registro
 * conserva el código, el cuerpo de la respuesta y la traza para diagnosticar después.
 */
export function reportApiError(context: string, error: unknown): void {
  const payload = buildApiFailurePayload(context, error);
  if (import.meta.env.DEV) {
    console.error('[api-error]', payload);
    return;
  }
  console.error('[error-tracking]', payload);
}
